<template>
  <main-page :user="user" color="green" hide-footer>
    <template v-slot:title> {{ title }} </template>
    <div class="container">
      <div class="row">
        <div class="col-12">
          <div class="card mt-4 text-center">
            <div class="card-header text-md success _600">
              {{ context.name }}
            </div>
            <div class="card-body d-flex flex-row text-md align-items-center">
              <div class="flex-grow">
                <div class="d-flex flex-row">
                  <div v-if="teachers" class="text-muted text-lg">
                    <i class="fas fa-user-graduate mr-2" />{{ teachers.length }}
                  </div>
                  <div v-if="students" class="text-muted text-lg ml-3">
                    <i class="fas fa-users mr-2" />{{ students ? students.length : 0 }}
                  </div>
                </div>
              </div>
              <div class="b-l pl-3">
                <a
                  class="btn btn-no-border btn-si-green d-flex flex-row align-items-center"
                  href="#/portfolio"
                >
                  Portfolio <i class="fas fa-lg fa-caret-right ml-2" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex flex-row px-3 mt-3 w-100">
          <button
            class="btn btn-no-border btn-si-yellow mr-2"
            :class="{ 'btn-si-selected': tab == 'roster' }"
            @click="tab = 'roster'"
          >
            Roster
          </button>
          <button
            class="btn btn-no-border btn-si-blue mr-2"
            :class="{ 'btn-si-selected': tab == 'teams' }"
            @click="tab = 'teams'"
          >
            Teams
          </button>
          <button
            class="btn btn-no-border btn-si-orange mr-2"
            :class="{ 'btn-si-selected': tab == 'assignments' }"
            @click="tab = 'assignments'"
          >
            Assignments
          </button>
          <button
            v-if="context.is_contest"
            class="btn btn-no-border btn-si-pink mr-2"
            :class="{ 'btn-si-selected': tab == 'pitch-contest' }"
            @click="tab = 'pitch-contest'"
          >
            Assign Judges
          </button>
          <button
            v-if="!context.is_contest"
            class="btn btn-no-border btn-si-yellow"
            :class="{ 'btn-si-selected': tab == 'activity' }"
            @click="tab = 'activity'"
          >
            Activity
          </button>
          <div class="horizontal-divider"></div>
          <button v-b-modal.joinCodeModal class="btn btn-no-border btn-si-green">
            Join Code...
          </button>
        </div>

        <!-- Roster tab -->
        <div
          v-if="tab == 'roster'"
          class="d-flex flex-row align-items-stretch w-100 mt-3"
        >
          <!-- Teachers -->
          <div class="col-4">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">{{ teacherLabel }}:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddTeacherModal" />
              </div>
              <div
                v-for="teacher in teachers"
                :key="teacher.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center text-md px-2">
                  <div class="flex-grow" style="max-width: 90%">
                    <div class="flex-grow text-left mt-1">
                      {{ fs.displayName(teacher.name) }}
                    </div>
                    <div class="text-muted text-left text-sm mb-2" style="line-height: 1">
                      {{ teacher.email }}
                    </div>
                  </div>

                  <i
                    class="fas fa-lg fa-trash btn btn-icon"
                    @click="removeTeacher(teacher)"
                    v-tippy
                    title="Remove"
                  />
                </div>
              </div>
              <div
                v-if="!(teachers && teachers.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No teachers
              </div>
            </div>
          </div>

          <!-- Students -->
          <div class="col-4" v-if="!context.is_contest">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Students:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddStudentModal" />
              </div>
              <div
                v-for="student in students"
                :key="student.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center text-md px-2">
                  <div class="flex-grow" style="max-width: 90%">
                    <div class="flex-grow text-left mt-1">
                      {{ fs.displayName(student.name) }}
                    </div>
                    <div class="text-muted text-left text-sm mb-2" style="line-height: 1">
                      {{ student.email }}
                    </div>
                  </div>

                  <i
                    class="fas fa-lg fa-trash btn btn-icon"
                    @click="removeStudent(student)"
                    v-tippy
                    title="Remove"
                  />
                </div>
              </div>
              <div
                v-if="!(students && students.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No students
              </div>
              <div v-if="studentInvites && studentInvites.length > 0" class="mt-3">
                <div class="si-text-blue">Pending Invites:</div>
                <div v-for="invite in studentInvites" :key="invite.id">
                  {{ invite.email }}
                </div>
              </div>
            </div>
          </div>

          <!-- Contest Teams -->
          <div class="col-4" v-if="context.is_contest">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Teams:</h3>
                <i
                  class="fas fa-lg fa-plus btn btn-icon"
                  @click="openAddContestTeamModal"
                />
              </div>
              <div
                v-for="team in teams"
                :key="team.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center textf-md px-2">
                  <div class="flex-grow" style="max-width: 90%">
                    <div class="flex-grow text-left mt-1">
                      {{ team.name }}
                    </div>
                    <div
                      class="text-muted text-left text-sm mb-2"
                      style="line-height: 1"
                      v-for="student in team.students"
                      :key="student.id"
                    >
                      {{ student.email }}
                    </div>
                    <div
                      class="text-muted text-left text-sm mb-2"
                      style="line-height: 1; font-style: italic"
                      v-for="student in teamInvites[team.id]"
                      :key="student.email"
                    >
                      {{ student.email }} (Pending Invite)
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="!(students && students.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No students
              </div>
            </div>
          </div>

          <!-- Partners -->
          <div class="col-4" v-if="!context.is_contest">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Partners:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddPartnerModal" />
              </div>
              <div
                v-for="partner in partners"
                :key="partner.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center text-md px-2">
                  <div class="flex-grow" style="max-width: 90%">
                    <div class="flex-grow text-left mt-1">
                      {{ fs.displayName(partner.name) }}
                    </div>
                    <div class="text-muted text-left text-sm mb-2" style="line-height: 1">
                      {{ partner.email }}
                    </div>
                  </div>

                  <i
                    class="fas fa-lg fa-trash btn btn-icon"
                    @click="removePartner(partner)"
                    v-tippy
                    title="Remove"
                  />
                </div>
              </div>
              <div
                v-if="!(partners && partners.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-3 mb-2"
              >
                No partners
              </div>
              <div v-if="partnerInvites && partnerInvites.length > 0" class="mt-3">
                <div class="si-text-blue">Pending Invites:</div>
                <div v-for="invite in partnerInvites" :key="invite.id">
                  {{ invite.email }}
                </div>
              </div>
            </div>
          </div>

          <!-- Judges -->
          <div class="col-4" v-if="context.is_contest">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Judges:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddJudgeModal" />
              </div>
              <div
                v-for="judge in judges"
                :key="judge.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center text-md px-2">
                  <div class="flex-grow" style="max-width: 90%">
                    <div class="flex-grow text-left mt-1">
                      {{ fs.displayName(judge.name) }}
                    </div>
                    <div class="text-muted text-left text-sm mb-2" style="line-height: 1">
                      {{ judge.email }}
                    </div>
                  </div>

                  <i
                    class="fas fa-lg fa-trash btn btn-icon"
                    @click="removeJudge(judge)"
                    v-tippy
                    title="Remove"
                  />
                </div>
              </div>
              <div
                v-if="!(judges && judges.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-3 mb-2"
              >
                No judges
              </div>
              <div v-if="judgeInvites && judgeInvites.length > 0" class="mt-3">
                <div class="si-text-blue">Pending Invites:</div>
                <div v-for="invite in judgeInvites" :key="invite.id">
                  {{ invite.email }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Teams tab -->
        <div
          v-if="tab == 'teams' && !context.is_contest"
          class="d-flex flex-row align-items-stretch w-100 mt-3"
        >
          <div class="col-4">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Teams:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddTeamModal" />
              </div>
              <div
                v-for="team in teams"
                :key="team.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center text-md mt-1 px-2">
                  <div class="flex-grow text-left">{{ team.name }}</div>
                  <i
                    class="fas fa-lg fa-edit btn btn-icon"
                    @click="openAddTeamModalExisting(team)"
                  />
                  <i
                    class="fas fa-lg fa-trash btn btn-icon"
                    @click="openDeleteTeamModal(team)"
                  />
                </div>
                <div class="px-2 mb-1 text-muted text-left">
                  {{ team.description }}
                </div>
              </div>
              <div
                v-if="!(teams && teams.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No teams
              </div>
            </div>
          </div>
          <div class="col-8">
            <div class="card box py-3 px-4">
              <h3 class="flex-grow text-left si-font my-1 pb-0">Students:</h3>
              <div
                v-for="student in students"
                :key="student.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center mt-1 px-2">
                  <div class="flex-grow">
                    <div
                      class="flex-grow text-left text-md"
                      style="padding-top: 5px; padding-bottom: 5px"
                    >
                      {{ fs.displayName(student.name) }}
                    </div>
                    <div class="mb-1 text-muted text-left">
                      {{ student.email }}
                    </div>
                  </div>
                  <div class="b-l pl-3">
                    <div
                      class="d-flex flex-row align-items-center"
                      style="margin-top: -5px"
                    >
                      <span class="pt-1 pr-2">Team:</span>
                      <i
                        v-if="!student.teams"
                        class="fas fa-lg fa-plus btn btn-icon"
                        @click="openAssignTeamModal(student)"
                        v-tippy
                        title="Assign Team"
                      />
                      <i
                        v-if="student.teams"
                        class="fas fa-lg fa-edit btn btn-icon"
                        @click="openAssignTeamModal(student)"
                        v-tippy
                        title="Change Team"
                      />
                    </div>
                    <div v-if="!student.teams || student.teams.length == 0" class="mb-1 text-very-muted pr-2">
                      ( None )
                    </div>
					<template v-else>
                      <div v-for="team in student.teams" :key="team.id" class="mb-1 text-muted pr-2">
                        {{ team.name }}
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div
                v-if="!(students && students.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No students
              </div>
            </div>
          </div>
        </div>

        <!-- Alternate Teams tab -->
        <div
          v-if="tab == 'teams' && context.is_contest"
          class="d-flex flex-row align-items-stretch w-100 mt-3"
        >
          <div class="col-12">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center"></div>
              <div
                v-for="team in teams"
                :key="team.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center text-md mt-1 px-2">
                  <div
                    class="flex-grow text-left"
                    style="text-overflow: ellipsis; overflow: hidden; white-space: nowrap"
                    v-if="team.name"
                  >
                    {{ team.name }}
                  </div>
                  <div
                    class="flex-grow text-left"
                    style="text-overflow: ellipsis; overflow: hidden; white-space: nowrap"
                    v-if="!team.name && team.students"
                  >
                    {{ nameList(team.students) }}
                  </div>
                  <div
                    class="flex-grow text-left"
                    style="text-overflow: ellipsis; overflow: hidden; white-space: nowrap"
                    v-if="!team.name && !team.students && teamInvites[team.id]"
                  >
                    {{ emailList(teamInvites[team.id]) }}
                  </div>

                  <button
                    class="btn btn-no-border btn-si-blue"
                    style="margin: 10px"
                    @click="openInviteMembersModal(team)"
                  >
                    Invite
                  </button>
                  <div class="d-flex flex-column">
                    <button
                      class="btn btn-no-border btn-si-green btn-si-icon"
                      v-tippy
                      title="Edit"
                    >
                      <i class="fas fa-edit" @click="openEditTeamModal(team)" />
                    </button>
                    <button
                      class="mt-1 btn btn-no-border btn-si-orange btn-si-icon"
                      v-tippy
                      title="Delete"
                    >
                      <i class="fas fa-trash" @click="openDeleteTeamModal(team)" />
                    </button>
                  </div>
                </div>
                <div class="px-2 mb-1 text-muted text-left">
                  {{ team.description }}
                </div>
              </div>
              <div
                v-if="!(teams && teams.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No teams
              </div>
            </div>
          </div>
        </div>

        <!-- Assignments tab -->
        <div
          v-if="tab == 'assignments'"
          class="row content d-flex flex-row align-items-stretch w-100 mt-3"
        >
          <div class="col-md-4" v-if="!context.is_contest">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Profiles:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddProfileModal" />
              </div>
              <div
                v-for="pa in profileAssignments"
                :key="pa.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center mt-1 px-2">
                  <div class="flex-grow">
                    <div class="text-md text-left">{{ pa.name }}</div>
                    <div class="text-muted text-left text-xs">
                      {{ fs.shortDate(pa.start) }} to
                      {{ fs.shortDate(pa.end) }}
                    </div>
                    <div class="text-muted text-left" v-html="pa.description"></div>
                  </div>

                  <div class="d-flex flex-column">
                    <button
                      class="btn btn-no-border btn-si-green btn-si-icon"
                      v-tippy
                      title="Edit"
                    >
                      <i class="fas fa-edit" @click="openEditProfileModal(pa)" />
                    </button>
                    <button
                      class="mt-1 btn btn-no-border btn-si-orange btn-si-icon"
                      v-tippy
                      title="Delete"
                    >
                      <i class="fas fa-trash" @click="openDeleteProfileModal(pa)" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-if="!(profileAssignments && profileAssignments.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No Profiles assigned
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="!context.is_contest">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Team Value Rubrics:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddTVRModal" />
              </div>
              <div
                v-for="tvra in tvrAssignments"
                :key="tvra.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center my-1 px-2">
                  <div class="flex-grow">
                    <div class="text-md text-left">{{ tvra.name }}</div>
                    <div class="text-muted text-left text-xs">
                      {{ fs.shortDate(tvra.start) }} to
                      {{ fs.shortDate(tvra.end) }}
                    </div>
                    <div class="text-muted text-left" v-html="tvra.description"></div>
                  </div>

                  <div class="d-flex flex-column">
                    <button
                      class="btn btn-no-border btn-si-green btn-si-icon"
                      v-tippy
                      title="Edit"
                    >
                      <i class="fas fa-edit" @click="openEditTVRModal(tvra)" />
                    </button>
                    <button
                      class="mt-1 btn btn-no-border btn-si-orange btn-si-icon"
                      v-tippy
                      title="Delete"
                    >
                      <i class="fas fa-trash" @click="openDeleteTVRModal(tvra)" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-if="!(tvrAssignments && tvrAssignments.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No Team Value Rubrics assigned
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Pitches:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddPitchModal" />
              </div>
              <div
                v-for="pa in pitchAssignments"
                :key="pa.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center my-1 px-2">
                  <div class="flex-grow">
                    <div class="text-md text-left">{{ pa.name }}</div>
                    <div class="text-muted text-left text-xs">
                      {{ fs.shortDate(pa.start) }} to
                      {{ fs.shortDate(pa.end) }}
                    </div>
                    <div class="text-muted text-left" v-html="pa.description"></div>
                  </div>

                  <div class="d-flex flex-column">
                    <button
                      class="btn btn-no-border btn-si-green btn-si-icon"
                      v-tippy
                      title="Edit"
                    >
                      <i class="fas fa-edit" @click="openEditPitchModal(pa)" />
                    </button>
                    <button
                      class="mt-1 btn btn-no-border btn-si-orange btn-si-icon"
                      v-tippy
                      title="Delete"
                    >
                      <i class="fas fa-trash" @click="openDeletePitchModal(pa)" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-if="!(pitchAssignments && pitchAssignments.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No Pitches assigned
              </div>
            </div>
          </div>
          <div
            class="col-md-4"
            v-if="!context.is_contest || (program.contest_config && program.contest_config.prototype)"
          >
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Prototypes:</h3>
                <i
                  class="fas fa-lg fa-plus btn btn-icon"
                  @click="openAddPrototypeModal"
                />
              </div>
              <div
                v-for="pa in prototypeAssignments"
                :key="pa.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center my-1 px-2">
                  <div class="flex-grow">
                    <div class="text-md text-left">{{ pa.name }}</div>
                    <div class="text-muted text-left text-xs">
                      {{ fs.shortDate(pa.start) }} to
                      {{ fs.shortDate(pa.end) }}
                    </div>
                    <div class="text-muted text-left" v-html="pa.description"></div>
                  </div>

                  <div class="d-flex flex-column">
                    <button
                      class="btn btn-no-border btn-si-green btn-si-icon"
                      v-tippy
                      title="Edit"
                    >
                      <i class="fas fa-edit" @click="openEditPrototypeModal(pa)" />
                    </button>
                    <button
                      class="mt-1 btn btn-no-border btn-si-orange btn-si-icon"
                      v-tippy
                      title="Delete"
                    >
                      <i class="fas fa-trash" @click="openDeletePrototypeModal(pa)" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-if="!(prototypeAssignments && prototypeAssignments.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No Prototypes assigned
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="!context.is_contest">
            <div class="card box py-3 px-4">
              <div class="d-flex flex-row align-items-center">
                <h3 class="flex-grow text-left si-font my-1 pb-0">Canvases:</h3>
                <i class="fas fa-lg fa-plus btn btn-icon" @click="openAddCanvasModal" />
              </div>
              <div
                v-for="ca in canvasAssignments"
                :key="ca.id"
                class="card mt-2"
                style="margin-left: -0.5rem; margin-right: -0.5rem"
              >
                <div class="d-flex flex-row align-items-center my-1 px-2">
                  <div class="flex-grow">
                    <div class="text-md text-left">{{ ca.name }}</div>
                    <div class="text-muted text-left text-xs">
                      {{ fs.shortDate(ca.start) }} to
                      {{ fs.shortDate(ca.end) }}
                    </div>
                    <div class="text-muted text-left" v-html="ca.description"></div>
                  </div>

                  <div class="d-flex flex-column">
                    <button
                      class="btn btn-no-border btn-si-green btn-si-icon"
                      v-tippy
                      title="Edit"
                    >
                      <i class="fas fa-edit" @click="openEditCanvasModal(ca)" />
                    </button>
                    <button
                      class="mt-1 btn btn-no-border btn-si-orange btn-si-icon"
                      v-tippy
                      title="Delete"
                    >
                      <i class="fas fa-trash" @click="openDeleteCanvasModal(ca)" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-if="!(canvasAssignments && canvasAssignments.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No Canvases assigned
              </div>
            </div>
          </div>
        </div>

        <!-- Contest tab -->
        <div
          v-if="tab == 'pitch-contest'"
          class="d-flex flex-row align-items-stretch w-100 mt-3"
        >
          <div class="col-12ß" style="overflow:scroll">
            <div class="card box py-3 px-4" style="overflow:scroll">
              <div class="d-flex flex-row align-items-center">
                <h3
                  class="flex-grow text-left si-font my-1 pb-0"
                  style="margin-right: 20px"
                >
                  Assign Judges:
                </h3>
              </div>
              <table>
                <tr>
                  <th></th>
                  <th
                    class="judge-header"
                    v-for="judge in allJudges"
                    :key="judge.id"
                  >
                    {{ nameWithEmail(judge) }}
                  </th>
                </tr>
                <tr>
                  <td class="judge-row-label" >Assign to All</td>
                  <td v-for="judge in allJudges" :key="judge.id">
                    <input type="checkbox" @change="toggleSelectAll($event, judge)" />
                  </td>
                </tr>
                <tr v-for="team in teams" :key="team.id">
                  <td class="judge-row-label">{{ teamLabel(team) }}</td>
                  <td v-for="judge in allJudges" :key="judge.id">
                    <input
                      :id="'checkbox'+team.id+judge.id"
                      type="checkbox"
                      v-model="teamJudgeGrid[team.id][judge.id]"
                    />
                  </td>
                </tr>
              </table>
              <div
                v-if="!(teams && teams.length > 0)"
                class="text-center text-very-muted text-lg _800 mt-2"
              >
                No teams
              </div>
              <div class="d-flex flex-row align-items-center mt-4">
                <button type="button" class="btn btn-primary" @click="saveJudges">
                  Save changes
                </button>
                </div>
            </div>
          </div>
        </div>

        <!-- Activity tab -->
        <div
          v-if="tab == 'activity'"
          class="d-flex flex-row align-items-stretch w-100 mt-3"
        >
          <b-table
            ref="table"
            :show-empty="true"
            :items="students"
            :fields="activityFields"
          >
            <template v-slot:cell(name)="data">
              {{ fs.displayName(data.item.name) }}
            </template>
            <template v-slot:cell(profiles)="data">
              {{ numProfiles(data.item.id) }}
            </template>
            <template v-slot:cell(pitches)="data">
              {{ numPitches(data.item.id) }}
            </template>
            <template v-slot:cell(prototypes)="data">
              {{ numPrototypes(data.item.id) }}
            </template>
            <template v-slot:cell(tvrs)="data">
              {{ numTVRs(data.item.id) }}
            </template>
            <template v-slot:cell(canvases)="data">
              {{ numCanvases(data.item.id) }}
            </template>
            <template v-slot:cell(actions)="data">
              <button
                v-if="!userSubmission(data.item.id)"
                :disabled="!portfolioReady(data.item.id)"
                class="mr-1 btn btn-inline btn-success"
                @click="openSubmitModal(data.item)"
              >
                Submit
              </button>
              <button
                v-else-if="!userSubmission(data.item.id).graded"
                class="mr-1 btn btn-inline btn-primary"
                @click="openUnsubmitModal(data.item)"
              >
                Un-submit
              </button>
              <button v-else class="mr-1 btn btn-inline btn-primary" disabled>
                Graded
              </button>
            </template>
          </b-table>
        </div>
      </div>
    </div>
    <!-- Add team modal -->
    <b-modal id="addTeamModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">
          {{ addTeamID ? "Edit Team" : "Add Team" }}
        </h3>
      </template>
      <div class="p-2">
        <div class="form-group pl-2 pr-3">
          <span v-if="!canAddTeam" class="float-right text-danger"
            >This team name is already in use</span
          >
          <label>Name:</label>
          <input
            type="text"
            v-model="addTeamName"
            class="form-control"
            :class="{ 'is-invalid': !canAddTeam }"
          />
        </div>
        <div class="form-group pl-2 pr-3">
          <label>Description:</label>
          <textarea v-model="addTeamDesc" class="form-control" />
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button
          type="button"
          class="btn"
          :class="{
            [addTeamName && canAddTeam ? 'btn-success' : 'btn-secondary']: true,
          }"
          :disabled="!addTeamName || !canAddTeam"
          @click="addTeam(ok)"
        >
          {{ addTeamID ? "Edit" : "Add" }}
        </button>
      </template>
    </b-modal>

    <!-- Delete team modal -->
    <b-modal id="deleteTeamModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Delete Team</h3>
      </template>
      <div v-if="deletingTeam" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to delete the team
            <br />
            <strong class="text-theme text-center">"{{ deletingTeam.name }}"</strong>?
          </h5>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            The operation will occur immediately, cannot be canceled or reversed, and will
            permanently delete all student work and scores associated with this team!
          </h6>
        </div>
        <div class="form-group pl-2 pr-3">
          <input type="text" v-model="deleteConfirm" class="form-control" />
          <small class="text-muted">To confirm, type the name of the team</small>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="deleteConfirm != deletingTeam.name"
          @click="deleteTeam(deletingTeam, ok)"
        >
          Delete
        </button>
      </template>
    </b-modal>

    <!-- Assign team modal -->
    <b-modal id="assignTeamModal" ref="assignTeamModal" headerClass="indigo" hide-footer>
      <template v-slot:modal-title>
        <h3 v-if="assigningStudent" class="modal-title full-bar-title">
          Assign
          <span class="text-muted">"{{ fs.displayName(assigningStudent.name) }}"</span>
          to Team
        </h3>
      </template>
      <div v-if="teams && teams.length > 0" class="d-flex flex-column p-2">
        <button
          v-for="team in teams"
          :key="team.id"
          class="btn btn-no-border btn-si-blue mb-3"
          @click="assignTeam(team)"
        >
          {{ team.name }}
        </button>
        <button class="btn btn-no-border btn-si-orange" @click="unassignTeam">
          (Unassign Team)
        </button>
      </div>
    </b-modal>

    <!-- Add Profile assignment modal -->
    <b-modal id="addProfileModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Add Profile Assignment</h3>
      </template>
      <div class="p-2">
        <div class="row mx-0">
          <div class="col-12 form-group pl-2 pr-3">
            <label>Name:</label>
            <input type="text" v-model="addProfileName" class="form-control" />
          </div>
          <div class="col-12 form-group pl-2 pr-3">
            <label>Description:</label>
            <RichText
              :text.sync="addProfileDesc"
              :initial="initAddProfileDesc"
              :options="{ height: 104 }"
            />
          </div>
          <div class="col-6 form-group px-2">
            <label>Window Start:</label>
            <date-picker v-model="addProfileStart" :config="dateConfig"></date-picker>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window End:</label>
            <date-picker v-model="addProfileEnd" :config="dateConfig"></date-picker>
          </div>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button
          v-if="!editProfileID"
          type="button"
          class="btn btn-success"
          @click="addProfile(ok)"
        >
          Add
        </button>
        <button
          v-if="editProfileID"
          type="button"
          class="btn btn-success"
          @click="addProfile(ok)"
        >
          Save
        </button>
      </template>
    </b-modal>

    <!-- Delete Profile assignment modal -->
    <b-modal id="deleteProfileModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Delete Profile Assignment</h3>
      </template>
      <div v-if="deletingProfile" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to delete the Profile assignment
            <br />
            <strong class="text-theme text-center">"{{ deletingProfile.name }}"</strong>?
          </h5>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            The operation will occur immediately, cannot be canceled or reversed, and will
            permanently delete all Profile responses associated with this assignment!
          </h6>
        </div>
        <div class="form-group pl-2 pr-3">
          <input type="text" v-model="deleteConfirm" class="form-control" />
          <small class="text-muted">To confirm, type the name of the assignment</small>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button
          type="button"
          class="btn btn-secondary"
          @click="
            deleteConfirm = null;
            cancel();
          "
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="deleteConfirm != deletingProfile.name"
          @click="deleteProfile(deletingProfile, ok)"
        >
          Delete
        </button>
      </template>
    </b-modal>

    <!-- Add Pitch assignment modal -->
    <b-modal id="addPitchModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Add Pitch Assignment</h3>
      </template>
      <div class="p-2">
        <div class="row mx-0">
          <div class="col-12 form-group pl-2 pr-3">
            <label>Name:</label>
            <input type="text" v-model="addPitchName" class="form-control" />
          </div>
          <div class="col-12 form-group pl-2 pr-3">
            <label>Description:</label>
            <RichText
              :text.sync="addPitchDesc"
              :initial="initAddPitchDesc"
              :options="{ height: 104 }"
            />
          </div>
          <div class="col-12 form-group">
            <label class="md-check check-md">
              <input type="checkbox" checked="checked" v-model="addPitchFinal" />
              <i class="blue"></i> Final Pitch
            </label>
          </div>
          <div class="col-12 form-group">
            <label class="md-check check-md">
              <input type="checkbox" v-model="addPitchHideFeedback" />
              <i class="blue"></i> Hide feedback from students
            </label>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window Start:</label>
            <date-picker v-model="addPitchStart" :config="dateConfig"></date-picker>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window End:</label>
            <date-picker v-model="addPitchEnd" :config="dateConfig"></date-picker>
          </div>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button
          v-if="!editPitchID"
          type="button"
          class="btn btn-success"
          @click="addPitch(ok)"
        >
          Add
        </button>
        <button
          v-if="editPitchID"
          type="button"
          class="btn btn-success"
          @click="addPitch(ok)"
        >
          Save
        </button>
      </template>
    </b-modal>

    <!-- Delete Pitch assignment modal -->
    <b-modal id="deletePitchModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Delete Pitch Assignment</h3>
      </template>
      <div v-if="deletingPitch" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to delete the Pitch assignment
            <br />
            <strong class="text-theme text-center">"{{ deletingPitch.name }}"</strong>?
          </h5>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            The operation will occur immediately, cannot be canceled or reversed, and will
            permanently delete all Pitch responses associated with this assignment!
          </h6>
        </div>
        <div class="form-group pl-2 pr-3">
          <input type="text" v-model="deleteConfirm" class="form-control" />
          <small class="text-muted">To confirm, type the name of the assignment</small>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button
          type="button"
          class="btn btn-secondary"
          @click="
            deleteConfirm = null;
            cancel();
          "
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="deleteConfirm != deletingPitch.name"
          @click="deletePitch(deletingPitch, ok)"
        >
          Delete
        </button>
      </template>
    </b-modal>

    <!-- Add Prototype assignment modal -->
    <b-modal id="addPrototypeModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Add Prototype Assignment</h3>
      </template>
      <div class="p-2">
        <div class="row mx-0">
          <div class="col-12 form-group pl-2 pr-3">
            <label>Name:</label>
            <input type="text" v-model="addPrototypeName" class="form-control" />
          </div>
          <div class="col-12 form-group pl-2 pr-3">
            <label>Description:</label>
            <RichText
              :text.sync="addPrototypeDesc"
              :initial="initAddPrototypeDesc"
              :options="{ height: 104 }"
            />
          </div>
          <div class="col-12 form-group">
            <label class="md-check check-md">
              <input type="checkbox" v-model="addPrototypeHideFeedback" />
              <i class="blue"></i> Hide feedback from students
            </label>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window Start:</label>
            <date-picker v-model="addPrototypeStart" :config="dateConfig"></date-picker>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window End:</label>
            <date-picker v-model="addPrototypeEnd" :config="dateConfig"></date-picker>
          </div>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button
          v-if="!editPrototypeID"
          type="button"
          class="btn btn-success"
          @click="addPrototype(ok)"
        >
          Add
        </button>
        <button
          v-if="editPrototypeID"
          type="button"
          class="btn btn-success"
          @click="addPrototype(ok)"
        >
          Save
        </button>
      </template>
    </b-modal>

    <!-- Delete Prototype assignment modal -->
    <b-modal id="deletePrototypeModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Delete Prototype Assignment</h3>
      </template>
      <div v-if="deletingPrototype" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to delete the Prototype assignment
            <br />
            <strong class="text-theme text-center">"{{ deletingPrototype.name }}"</strong
            >?
          </h5>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            The operation will occur immediately, cannot be canceled or reversed, and will
            permanently delete all Prototype responses associated with this assignment!
          </h6>
        </div>
        <div class="form-group pl-2 pr-3">
          <input type="text" v-model="deleteConfirm" class="form-control" />
          <small class="text-muted">To confirm, type the name of the assignment</small>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button
          type="button"
          class="btn btn-secondary"
          @click="
            deleteConfirm = null;
            cancel();
          "
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="deleteConfirm != deletingPrototype.name"
          @click="deletePrototype(deletingPrototype, ok)"
        >
          Delete
        </button>
      </template>
    </b-modal>

    <!-- Add TVR assignment modal -->
    <b-modal id="addTVRModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Add TVR Assignment</h3>
      </template>
      <div class="p-2">
        <div class="row mx-0">
          <div class="col-12 form-group pl-2 pr-3">
            <label>Name:</label>
            <input type="text" v-model="addTVRName" class="form-control" />
          </div>
          <div class="col-12 form-group pl-2 pr-3">
            <label>Description:</label>
            <RichText
              :text.sync="addTVRDesc"
              :initial="initAddTVRDesc"
              :options="{ height: 104 }"
            />
          </div>
          <div class="col-12 form-group">
            <label class="md-check check-md">
              <input type="checkbox" v-model="addTVRHideFeedbackS" />
              <i class="blue"></i> Hide feedback from students
            </label>
            <label class="md-check check-md">
              <input type="checkbox" v-model="addTVRHideFeedbackP" />
              <i class="blue"></i> Hide feedback from community partners
            </label>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window Start:</label>
            <date-picker v-model="addTVRStart" :config="dateConfig"></date-picker>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window End:</label>
            <date-picker v-model="addTVREnd" :config="dateConfig"></date-picker>
          </div>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button
          v-if="!editTVRID"
          type="button"
          class="btn btn-success"
          @click="addTVR(ok)"
        >
          Add
        </button>
        <button
          v-if="editTVRID"
          type="button"
          class="btn btn-success"
          @click="addTVR(ok)"
        >
          Save
        </button>
      </template>
    </b-modal>

    <!-- Delete TVR assignment modal -->
    <b-modal id="deleteTVRModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Delete TVR Assignment</h3>
      </template>
      <div v-if="deletingTVR" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to delete the Team Value Rubric assignment
            <br />
            <strong class="text-theme text-center">"{{ deletingTVR.name }}"</strong>?
          </h5>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            The operation will occur immediately, cannot be canceled or reversed, and will
            permanently delete all Team Value Rubric responses associated with this
            assignment!
          </h6>
        </div>
        <div class="form-group pl-2 pr-3">
          <input type="text" v-model="deleteConfirm" class="form-control" />
          <small class="text-muted">To confirm, type the name of the assignment</small>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button
          type="button"
          class="btn btn-secondary"
          @click="
            deleteConfirm = null;
            cancel();
          "
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="deleteConfirm != deletingTVR.name"
          @click="deleteTVR(deletingTVR, ok)"
        >
          Delete
        </button>
      </template>
    </b-modal>

    <!-- Add Canvas assignment modal -->
    <b-modal id="addCanvasModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Add Canvas Assignment</h3>
      </template>
      <div class="p-2">
        <div class="row mx-0">
          <div class="col-12 form-group pl-2 pr-3">
            <label>Name:</label>
            <input type="text" v-model="addCanvasName" class="form-control" />
          </div>
          <div class="col-12 form-group pl-2 pr-3">
            <label>Description:</label>
            <RichText
              :text.sync="addCanvasDesc"
              :initial="initAddCanvasDesc"
              :options="{ height: 104 }"
            />
          </div>
          <div class="col-12 form-group">
            <label class="md-check check-md">
              <input type="checkbox" v-model="addCanvasHidePartner" />
              <i class="blue"></i> Hide canvas from community partners
            </label>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window Start:</label>
            <date-picker v-model="addCanvasStart" :config="dateConfig"></date-picker>
          </div>
          <div class="col-6 form-group px-2">
            <label>Window End:</label>
            <date-picker v-model="addCanvasEnd" :config="dateConfig"></date-picker>
          </div>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button
          v-if="!editCanvasID"
          type="button"
          class="btn btn-success"
          @click="addCanvas(ok)"
        >
          Add
        </button>
        <button
          v-if="editCanvasID"
          type="button"
          class="btn btn-success"
          @click="addCanvas(ok)"
        >
          Save
        </button>
      </template>
    </b-modal>

    <!-- Delete Canvas assignment modal -->
    <b-modal id="deleteCanvasModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Delete Canvas Assignment</h3>
      </template>
      <div v-if="deletingCanvas" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to delete the Canvas assignment
            <br />
            <strong class="text-theme text-center">"{{ deletingCanvas.name }}"</strong>?
          </h5>
          <h6 class="py-2">
            <span class="text-danger far faw fa-exclamation-triangle"></span>
            The operation will occur immediately, cannot be canceled or reversed, and will
            permanently delete all Canvas responses associated with this assignment!
          </h6>
        </div>
        <div class="form-group pl-2 pr-3">
          <input type="text" v-model="deleteConfirm" class="form-control" />
          <small class="text-muted">To confirm, type the name of the assignment</small>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button
          type="button"
          class="btn btn-secondary"
          @click="
            deleteConfirm = null;
            cancel();
          "
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="deleteConfirm != deletingCanvas.name"
          @click="deleteCanvas(deletingCanvas, ok)"
        >
          Delete
        </button>
      </template>
    </b-modal>

    <!-- Edit Team Modal -->
    <b-modal id="editTeamModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Team Details</h3>
      </template>
      <div class="p-2">
        <div class="row mx-0">
          <div class="col-12 form-group pl-2 pr-3">
            <label>Name:</label>
            <input type="text" v-model="editingTeamName" class="form-control" />
          </div>
          <div class="col-12 form-group pl-2 pr-3">
            <label>Description:</label>
            <textarea v-model="editingTeamDescription" class="form-control" />
          </div>
          <div class="col-12 form-group pl-2 pr-3">
            <label>Students:</label>
            <div
              v-for="student in editingTeamStudents"
              :key="student.id"
              style="margin: 10px"
            >
              <div>
                {{ fs.displayName(student.name) }}
              </div>
              <div>{{ student.email }}</div>
            </div>
          </div>

          <div class="form-group pl-2 pr-3">
            <label>Join Code</label>
            <input
              type="text"
              v-model="editingTeamJoinCode"
              readonly
              class="form-control join-code"
            />
          </div>
          <button
            type="button"
            class="ml-2 btn btn-success"
            @click="openRegenerateModal(regenerateTeamCode)"
          >
            <i class="fas fa-sync mr-2" />Regenerate Code
          </button>
        </div>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button type="button" class="btn btn-success" @click="saveTeam(ok)">Save</button>
      </template>
    </b-modal>

    <!-- Join code modal -->
    <b-modal id="joinCodeModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Join Codes</h3>
      </template>
      <div class="p-2">
        <div v-if="!context.is_contest">
          <div class="form-group pl-2 pr-3">
            <label>Student Join Code</label>
            <input
              type="text"
              v-model="context.join_code_student"
              readonly
              class="form-control join-code"
            />
          </div>
          <button
            type="button"
            class="ml-2 btn btn-success"
            @click="openRegenerateModal(regenerateStudentCode)"
          >
            <i class="fas fa-sync mr-2" />Regenerate Code
          </button>
        </div>
        <div v-if="!context.is_contest">
          <div class="mt-4 form-group pl-2 pr-3">
            <label>Partner Join Code</label>
            <input
              type="text"
              v-model="context.join_code_partner"
              readonly
              class="form-control join-code"
            />
          </div>
          <button
            type="button"
            class="ml-2 btn btn-success"
            @click="openRegenerateModal(regeneratePartnerCode)"
          >
            <i class="fas fa-sync mr-2" />Regenerate Code
          </button>
        </div>
        <div v-if="context.is_contest">
          <div class="form-group pl-2 pr-3">
            <label>Judge Join Code</label>
            <input
              type="text"
              v-model="context.join_code_judge"
              readonly
              class="form-control join-code"
            />
          </div>
          <button
            type="button"
            class="ml-2 btn btn-success"
            @click="openRegenerateModal(regenerateJudgeCode)"
          >
            <i class="fas fa-sync mr-2" />Regenerate Code
          </button>
        </div>
      </div>
      <template v-slot:modal-footer="{ _, cancel }">
        <button type="button" class="btn btn-primary" @click="cancel">Done</button>
      </template>
    </b-modal>

    <!--- Confirm Submit modal -->
    <b-modal id="submitModal" headerClass="green">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Submit Portfolio</h3>
      </template>
      <div v-if="submittingStudent" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to submit {{ fs.displayName(submittingStudent.name) }}'s
            portfolio?
          </h5>
        </div>
      </div>
      <template v-slot:modal-footer="{ _, cancel }">
        <button
          type="button"
          class="btn btn-primary"
          @click="submit(submittingStudent.id)"
        >
          Submit
        </button>
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
      </template>
    </b-modal>

    <!--- Confirm Unsubmit modal -->
    <b-modal id="unsubmitModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Un-submit portfolio</h3>
      </template>
      <div v-if="submittingStudent" class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">
            Are you sure you want to un-submit
            {{ fs.displayName(submittingStudent.name) }}'s portfolio?
          </h5>
        </div>
      </div>
      <template v-slot:modal-footer="{ _, cancel }">
        <button
          type="button"
          class="btn btn-primary"
          @click="unsubmit(submittingStudent.id)"
        >
          Un-submit
        </button>
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
      </template>
    </b-modal>

    <b-modal id="regenJoinCodeModal" headerClass="indigo">
      <template v-slot:modal-title>
        <h3 class="modal-title full-bar-title">Regenerate Join Code</h3>
      </template>
      <div class="p-2">
        <div class="row px-4 py-2 text-md">
          <h5 class="py-2">Are you sure you want to regenerate this join code?</h5>
          <h6 class="py-2">All existing invitations will become invalid.</h6>
        </div>
      </div>
      <template v-slot:modal-footer="{ _, cancel }">
        <button type="button" class="btn btn-secondary" @click="cancel">Cancel</button>
        <button type="button" class="btn btn-primary" @click="regenFunc">
          Regenerate Code
        </button>
      </template>
    </b-modal>

    <!-- Add Teacher modal -->
    <user-search-modal
      id="addTeacherModal"
      role="TEACHER"
      :existing="teachers"
      @picked="addTeacher"
    />

    <!-- Add Student modal -->
    <user-search-modal
      id="addStudentModal"
      role="STUDENT"
      :existing="students"
      canInvite
      :joinCode="context.join_code_student"
      @picked="addStudent"
      @inviteSent="inviteSent"
    />

    <!-- Add Partner modal -->
    <user-search-modal
      id="addPartnerModal"
      role="PARTNER"
      :existing="partners"
      canInvite
      :joinCode="context.join_code_partner"
      @picked="addPartner"
      @inviteSent="inviteSent"
    />

    <!-- Add Judge modal -->
    <user-search-modal
      id="addJudgeModal"
      role="JUDGE"
      :existing="judges"
      canInvite
      :joinCode="context.join_code_judge"
      @picked="addJudge"
      @inviteSent="inviteSentJudge"
    />

    <!-- Add Team modal -->
    <multi-user-search-modal
      id="addContestTeamModal"
      groupName="Team"
      role="STUDENT"
      @save="inviteTeam"
    />

    <!-- Invite Members modal -->
    <user-search-modal
      id="inviteMembersModal"
      role="STUDENT"
      :existing="editingTeamStudents"
      canInvite
      :teamid="editingTeamId"
      :joinCode="editingTeamJoinCode"
      @picked="addTeamMember"
      @inviteSent="inviteSent"
    />

    <template v-slot:footer>
      <div class="col-6 text-right">
        <a class="btn btn-si-secondary" href="#/classes">Back</a>
      </div>
    </template>
  </main-page>
</template>

<script>
import "eonasdan-bootstrap-datetimepicker/build/css/bootstrap-datetimepicker.css";
import DatePicker from "vue-bootstrap-datetimepicker";
import RichText from "@/components/RichText";
import MainPage from "@/components/MainPage";
import UserSearchModal from "@/components/UserSearchModal";
import MultiUserSearchModal from "@/components/MultiUserSearchModal";

import SectionService from "@/services/SectionService";
import TeamService from "@/services/TeamService";
import AssignmentService from "@/services/AssignmentService";
import BrandService from "@/services/BrandService";
import ProfileService from "@/services/ProfileService";
import PitchService from "@/services/PitchService";
import TVRService from "@/services/TVRService";
import CanvasService from "@/services/CanvasService";
import ProgramService from "@/services/ProgramService";

import fs from "@/services/FormatService";
import Notie from "@/services/NotieService";
import BB from "bluebird";
import _ from "lodash";
import moment from "moment";

export default {
  name: "ClassDetails",

  props: ["user"],

  components: {
    MainPage,
    DatePicker,
    RichText,
    UserSearchModal,
    MultiUserSearchModal,
  },

  data() {
    return {
      fs: fs,
      loading: true,
      dateConfig: fs.dateConfig,

      tab: "roster",
      deleteConfirm: null,

      program: null,

      context: null,
      teachers: null,
      students: null,
      invites: null,
      studentInvites: null,
      partnerInvites: null,
      judgeInvites: null,
      teamInvites: null,
      teams: null,
      profileAssignments: null,
      pitchAssignments: null,
      prototypeAssignments: null,
      tvrAssignments: null,

      addProfileName: null,
      addProfileDesc: null,
      addProfileStart: null,
      addProfileEnd: null,
      editProfileID: null,
      deletingProfile: null,

      addPitchName: null,
      addPitchDesc: null,
      addPitchFinal: false,
      addPitchHideFeedback: false,
      addPitchStart: null,
      addPitchEnd: null,
      editPitchID: null,
      deletingPitch: null,

      addPrototypeName: null,
      addPrototypeDesc: null,
      addPrototypeHideFeedback: false,
      addPrototypeStart: null,
      addPrototypeEnd: null,
      editPrototypeID: null,
      deletingPrototype: null,

      addTVRName: null,
      addTVRDesc: null,
      addTVRStart: null,
      addTVREnd: null,
      addTVRHideFeedbackS: null,
      addTVRHideFeedbackP: null,
      editTVRID: null,
      deletingTVR: null,

      addCanvasName: null,
      addCanvasDesc: null,
      addCanvasStart: null,
      addCanvasEnd: null,
      addCanvasHidePartner: null,
      editCanvasID: null,
      deletingCanvas: null,

      addTeamName: null,
      addTeamDesc: null,
      addTeamID: null,
      addTeamUsers: null,
      deletingTeam: null,
      editingTeam: null,
      editingTeamId: null,
      editingTeamStudents: null,
      editingTeamJoinCode: null,
      editingTeamName: null,
      editingTeamDescription: null,
      assigningStudent: null,

      teacherFilter: null,
      searchedTeachers: null,

      studentFilter: null,
      searchedStudents: null,

      partnerFilter: null,
      searchedPartners: null,

      judgeFilter: null,
      searchedJudges: null,

      initAddProfileDesc: "",
      initAddPitchDesc: "",
      initAddPrototypeDesc: "",
      initAddTVRDesc: "",
      initAddCanvasDesc: "",

      regenFunc: {},

      brand: BrandService.getBrand(),

      activityFields: [
        { key: "name", label: "Student", sortable: true },
        { key: "profiles", label: "Profiles" },
        { key: "pitches", label: "Pitches" },
        { key: "prototypes", label: "Prototypes" },
        { key: "tvrs", label: "TVRs" },
        { key: "canvases", label: "Canvases" },
        { key: "actions", label: "Actions" },
      ],

      profiles: [],
      pitches: [],
      prototypes: [],
      tvrs: [],
      canvases: [],

      submissions: null,
      submittingStudent: null,

      teamJudgeGrid: {},
    };
  },

  created() {
    this.setContextDetails(this.user.context);
    this.loadData();
  },

  computed: {
    canAddTeam() {
      if (!this.teams) {
        return true;
      }
      if (this.addTeamID != null) {
        return true;
      }

      for (let team of this.teams) {
        if (team.name == this.addTeamName) return false;
      }

      return true;
    },
    title() {
      if (this.context.is_contest) {
        return "CONTEST DETAILS";
      } else {
        return "CLASS DETAILS";
      }
    },
    teacherLabel() {
      if (this.context.is_contest) {
        return "Owners";
      } else {
        return "Teachers";
      }
    }
  },

  methods: {
    toggleSelectAll(e, judge) {
      this.$nextTick(() => {
          _.each(this.teams, (team) => {
            document.getElementById('checkbox'+team.id+judge.id).checked = e.path[0].checked;
            this.teamJudgeGrid[team.id][judge.id] = e.path[0].checked;
          })
      })
    },

    datesOK(startString, endString) {
      var startDate = moment(startString);
      var endDate = moment(endString);
      if (startDate >= endDate) {
        Notie.error("End date must be later than start date");
        return false;
      }
      if (endDate < moment()) {
        Notie.error("Due date must be a future date");
        return false;
      }
      if (!startString || !endString) {
        Notie.error("Valid dates required");
        return false;
      }
      return true;
    },

    nameWithEmail({ name, email }) {
      var displayName = fs.displayName(name);
      if (displayName != "(Unknown)") {
        return displayName + ": " + email;
      } else {
        return email;
      }
    },

    teamLabel(team) {
      if (team.name) {
        return team.name;
      } else {
        return this.nameList(team.students);
      }
    },

    loadData() {
      this.loadActivityData();
      this.loadProgram();
      this.loadSubmissions();
    },

    loadActivityData() {
      BB.props({
        profiles: ProfileService.getSectionProfiles(this.context.id),
        pitches: PitchService.getContextPitchesPrefilled(),
        tvrs: TVRService.getContextFeedbacks(),
        canvases: CanvasService.getContextCanvasesPrefilled(),
      })
        .then((r) => {
          this.profiles = r.profiles.data.filter((p) => {
            for (var a of this.profileAssignments) {
              if (a.id == p.assignment_id) {
                return true;
              }
            }
          });
          this.pitches = r.pitches.data.filter((p) => {
            for (var a of this.pitchAssignments) {
              if (a.id == p.assignment_id) {
                return true;
              }
            }
          });
          this.prototypes = r.pitches.data.filter((p) => {
            if (this.prototypeAssignments) {
              for (var a of this.prototypeAssignments) {
                if (a.id == p.assignment_id) {
                  return true;
                }
              }
            }
          });
          this.tvrs = r.tvrs.data.filter((tvr) => {
            for (var a of this.tvrAssignments) {
              if (a.id == tvr.assignment_id) {
                return true;
              }
            }
          });
          this.canvases = r.canvases.data.filter((c) => {
            for (var a of this.canvasAssignments) {
              if (a.id == c.assignment_id) {
                return true;
              }
            }
          });
        })
        .catch((e) => {
          Notie.error("Failed to load activity data", e);
        });
    },

    loadProgram() {
      ProgramService.getContextProgram()
        .then((r) => {
          this.program = r.data;
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to load program", e);
        });
    },

    loadSubmissions() {
      SectionService.getContextSubmissions()
        .then((r) => {
          this.submissions = r.data;
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to load portfolio submissions", e);
        });
    },

    userSubmission(userID) {
      return _.find(this.submissions, {
        student_id: userID,
      });
    },

    loadContext() {
      // Refresh the user to get new context info
      SectionService.getContextPrefilled()
        .then((r) => {
          console.log("GOT CONTEXT", r.data);
          this.setContextDetails(r.data);
          this.loadData();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to load context", e);
        });
    },

    setContextDetails(context) {
      this.context = context;
      console.log(context);
      this.teachers = fs.sortByName(this.context.teachers);
      this.students = fs.sortByName(this.context.students);
      this.partners = fs.sortByName(this.context.partners);
      this.judges = fs.sortByName(this.context.judges);
      this.invites = this.context.invites;
      this.teams = this.context.teams;
      this.profileAssignments = this.context.profiles;
      this.pitchAssignments = this.context.pitches;
      this.prototypeAssignments = this.context.prototypes;
      this.tvrAssignments = this.context.tvrs;
      this.canvasAssignments = this.context.canvases;

      _.each(this.teams, (team) => {
        team.students = _.filter(team.students, (s) => {
          return this.context.student_ids.includes(s.id);
        });
      });

      let studentTeamMap = {};
      _.each(this.teams, (team) => {
        _.each(team.user_ids, (uid) => {
          if(!studentTeamMap[uid]) {
            studentTeamMap[uid] = []
          }
          studentTeamMap[uid].push(team);
        });
      });

      this.teamInvites = {};
      _.each(this.teams, (team) => {
        this.teamInvites[team.id] = team.invites;
      });

      _.each(this.students, (student) => {
        student.teams = studentTeamMap[student.id];
      });

      this.studentInvites = _.filter(this.invites, { role: "STUDENT" });
      this.partnerInvites = _.filter(this.invites, { role: "PARTNER" });
      this.judgeInvites = _.filter(this.invites, { role: "JUDGE" });

      if (this.context.judges) {
        this.allJudges = this.context.judges.concat(this.judgeInvites);
      } else {
        this.allJudges = this.judgeInvites;
      }

      _.each(this.teams, (team) => {
        this.teamJudgeGrid[team.id] = {}
        _.each(this.allJudges, (judge) => {
          this.teamJudgeGrid[team.id][judge.id] = team.judge_ids.includes(judge.id)
        })
      })

      if (this.editingTeam != null) {
        this.editingTeam = this.teams.filter((team) => team.id == this.editingTeam.id)[0];
        this.editingTeamStudents = this.editingTeam.students;
        this.editingTeamJoinCode = this.editingTeam.join_code;
        this.editingTeamName = this.editingTeam.name;
        this.editingTeamDescription = this.editingTeam.description;
      }
    },

    blankTeam() {
      let team = {
        name: "",
        district_id: this.user.district_id,
        section_id: this.context.id,
      };

      return team;
    },

    openAddTeamModal() {
      this.addTeamName = null;
      this.addTeamDesc = null;
      this.addTeamID = null;
      this.$bvModal.show("addTeamModal");
    },

    openAddTeamModalExisting(existingTeam) {
      this.addTeamName = existingTeam.name;
      this.addTeamDesc = existingTeam.description;
      this.addTeamID = existingTeam.id;
      this.addTeamUsers = existingTeam.user_ids;
      this.$bvModal.show("addTeamModal");
    },

    addTeam() {
      let team = this.blankTeam();
      team.name = this.addTeamName;
      team.description = this.addTeamDesc;
      team.id = this.addTeamID;
      team.user_ids = this.addTeamUsers;

      TeamService.addTeam(team)
        .then(() => {
          if (this.addTeamID == null) {
            Notie.success(`Added team ${team.name}`);
          } else {
            Notie.success(`Updated team ${team.name}`);
          }
          this.loadContext();
          this.$bvModal.hide("addTeamModal");
        })
        .catch((e) => {
          console.log(e);
          if (this.addTeamID == null) {
            Notie.success(`Failed to add team`);
          } else {
            Notie.success(`Failed to update team`);
          }
        });
    },

    openDeleteTeamModal(team) {
      this.deletingTeam = team;
      this.deleteConfirm = "";
      this.$bvModal.show("deleteTeamModal");
    },

    deleteTeam(team) {
      TeamService.deleteTeam(team.id)
        .then(() => {
          Notie.success(`Deleted team ${team.name}`);
          this.loadContext();
          this.$bvModal.hide("deleteTeamModal");
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to delete team", e);
        });
    },

    openAssignTeamModal(student) {
      this.assigningStudent = student;
      this.$bvModal.show("assignTeamModal");
    },

    assignTeam(team) {
      TeamService.assignTeam(this.context.id, this.assigningStudent.id, team.id)
        .then(() => {
          Notie.success(
            `Assigned ${fs.displayName(this.assigningStudent)} to team ${team.name}`
          );
          this.loadContext();
          this.$bvModal.hide("assignTeamModal");
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to assign team");
        });
    },

    unassignTeam() {
      TeamService.unassignTeam(this.context.id, this.assigningStudent.id)
        .then(() => {
          Notie.success(
            `Unassigned ${fs.displayName(this.assigningStudent)} from any teams`
          );
          this.loadContext();
          this.$bvModal.hide("assignTeamModal");
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to unassign team");
        });
    },

    blankProfileAssignment() {
      let pa = {
        name: "New Profile",
        district_id: this.user.district_id,
        section_id: this.context.id,
      };

      return pa;
    },

    openAddProfileModal() {
      this.addProfileName = null;
      this.addProfileDesc = null;
      this.addProfileStart = null;
      this.addProfileEnd = null;
      this.editProfileID = null;
      this.initAddProfileDesc = "";
      this.$bvModal.show("addProfileModal");
    },

    openEditProfileModal(pa) {
      this.addProfileName = pa.name;
      this.addProfileDesc = pa.description;
      this.addProfileStart = pa.start;
      this.addProfileEnd = pa.end;
      this.editProfileID = pa.id;
      this.initAddProfileDesc = pa.description;
      this.$bvModal.show("addProfileModal");
    },

    addProfile() {
      let pa = this.blankProfileAssignment();
      pa.name = this.addProfileName;
      pa.description = this.addProfileDesc;
      pa.start = this.addProfileStart;
      pa.end = this.addProfileEnd;
      pa.id = this.editProfileID;

      if (this.datesOK(pa.start, pa.end)) {
        AssignmentService.saveProfileAssignment(pa)
          .then(() => {
            if (pa.id) {
              Notie.success("Edited Profile assignment", pa.name);
            } else {
              Notie.success("Added Profile assignment", pa.name);
            }
            this.loadContext();
            this.$bvModal.hide("addProfileModal");
          })
          .catch((e) => {
            console.log(e);
            if (pa.id) {
              Notie.error("Failed to edit Profile assignment", e);
            } else {
              Notie.error("Failed to add Profile assignment", e);
            }
          });
      }
    },

    openDeleteProfileModal(pa) {
      this.deletingProfile = pa;
      this.$bvModal.show("deleteProfileModal");
    },

    deleteProfile(pa) {
      AssignmentService.deleteProfileAssignment(pa.id)
        .then(() => {
          Notie.success("Deleted Profile assignment", pa.name);
          this.loadContext();
          this.$bvModal.hide("deleteProfileModal");
          this.deleteConfirm = null;
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to delete Profile assignment", e);
        });
    },

    blankPitchAssignment() {
      let pa = {
        name: "New Pitch",
        district_id: this.user.district_id,
        section_id: this.context.id,
      };

      return pa;
    },

    openAddPitchModal() {
      this.addPitchName = null;
      this.addPitchDesc = null;
      this.addPitchFinal = false;
      this.addPitchHideFeedback = false;
      this.addPitchStart = null;
      this.addPitchEnd = null;
      this.editPitchID = null;
      this.initAddPitchDesc = "";
      this.$bvModal.show("addPitchModal");
    },

    openEditPitchModal(pa) {
      this.addPitchName = pa.name;
      this.addPitchDesc = pa.description;
      this.addPitchFinal = pa.final;
      this.addPitchHideFeedback = pa.hide_feedback;
      this.addPitchStart = pa.start;
      this.addPitchEnd = pa.end;
      this.editPitchID = pa.id;
      this.initAddPitchDesc = pa.description;
      this.$bvModal.show("addPitchModal");
    },

    addPitch() {
      let pa = this.blankPitchAssignment();
      pa.name = this.addPitchName;
      pa.description = this.addPitchDesc;
      pa.hide_feedback = this.addPitchHideFeedback;
      pa.start = this.addPitchStart;
      pa.end = this.addPitchEnd;
      pa.id = this.editPitchID;

      if (this.datesOK(pa.start, pa.end)) {
        AssignmentService.savePitchAssignment(pa)
          .then(() => {
            if (pa.id) {
              Notie.success("Edited Pitch assignment", pa.name);
            } else {
              Notie.success("Added Pitch assignment", pa.name);
            }
            this.loadContext();
            this.$bvModal.hide("addPitchModal");
          })
          .catch((e) => {
            console.log(e);
            if (pa.id) {
              Notie.error("Failed to edit Pitch assignment", e);
            } else {
              Notie.error("Failed to add Pitch assignment", e);
            }
          });
      }
    },

    openDeletePitchModal(pa) {
      this.deletingPitch = pa;
      this.$bvModal.show("deletePitchModal");
    },

    deletePitch(pa) {
      AssignmentService.deletePitchAssignment(pa.id)
        .then(() => {
          Notie.success("Deleted Pitch assignment", pa.name);
          this.loadContext();
          this.$bvModal.hide("deletePitchModal");
          this.deleteConfirm = null;
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to delete Pitch assignment", e);
        });
    },

    blankPrototypeAssignment() {
      let pa = {
        name: "New Prototype",
        district_id: this.user.district_id,
        section_id: this.context.id,
      };

      return pa;
    },

    openAddPrototypeModal() {
      this.addPrototypeName = null;
      this.addPrototypeDesc = null;
      this.addPrototypeHideFeedback = null;
      this.addPrototypeStart = null;
      this.addPrototypeEnd = null;
      this.editPrototypeID = null;
      this.initAddPrototypeDesc = "";
      this.$bvModal.show("addPrototypeModal");
    },

    openEditPrototypeModal(pa) {
      this.addPrototypeName = pa.name;
      this.addPrototypeDesc = pa.description;
      this.addPrototypeHideFeedback = pa.hide_feedback;
      this.addPrototypeStart = pa.start;
      this.addPrototypeEnd = pa.end;
      this.editPrototypeID = pa.id;
      this.initAddPrototypeDesc = pa.description;
      this.$bvModal.show("addPrototypeModal");
    },

    addPrototype() {
      let pa = this.blankPrototypeAssignment();
      pa.name = this.addPrototypeName;
      pa.description = this.addPrototypeDesc;
      pa.hide_feedback = this.addPrototypeHideFeedback;
      pa.start = this.addPrototypeStart;
      pa.end = this.addPrototypeEnd;
      pa.id = this.editPrototypeID;

      if (this.datesOK(pa.start, pa.end)) {
        AssignmentService.savePrototypeAssignment(pa)
          .then(() => {
            if (pa.id) {
              Notie.success("Edited Prototype assignment", pa.name);
            } else {
              Notie.success("Added Prototype assignment", pa.name);
            }
            this.loadContext();
            this.$bvModal.hide("addPrototypeModal");
          })
          .catch((e) => {
            console.log(e);
            if (pa.id) {
              Notie.error("Failed to edit Prototype assignment", e);
            } else {
              Notie.error("Failed to add Prototype assignment", e);
            }
          });
      }
    },

    openDeletePrototypeModal(pa) {
      this.deletingPrototype = pa;
      this.$bvModal.show("deletePrototypeModal");
    },

    deletePrototype(pa) {
      AssignmentService.deletePrototypeAssignment(pa.id)
        .then(() => {
          Notie.success("Deleted Prototype assignment", pa.name);
          this.loadContext();
          this.$bvModal.hide("deletePrototypeModal");
          this.deleteConfirm = null;
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to delete Prototype assignment", e);
        });
    },

    blankTVRAssignment() {
      let tvra = {
        name: "New Team Value Rubric",
        district_id: this.user.district_id,
        section_id: this.context.id,
      };

      return tvra;
    },

    openAddTVRModal() {
      this.addTVRName = null;
      this.addTVRDesc = null;
      this.addTVRHideFeedbackS = false;
      this.addTVRHideFeedbackP = true;
      this.addTVRStart = null;
      this.addTVREnd = null;
      this.editTVRID = null;
      this.initAddTVRDesc = "";
      this.$bvModal.show("addTVRModal");
    },

    openEditTVRModal(tvra) {
      this.addTVRName = tvra.name;
      this.addTVRDesc = tvra.description;
      this.addTVRHideFeedbackS = tvra.hide_feedback;
      this.addTVRHideFeedbackP = !tvra.show_feedback_partner;
      this.addTVRStart = tvra.start;
      this.addTVREnd = tvra.end;
      this.editTVRID = tvra.id;
      this.initAddTVRDesc = tvra.description;
      this.$bvModal.show("addTVRModal");
    },

    addTVR() {
      let tvra = this.blankTVRAssignment();
      tvra.name = this.addTVRName;
      tvra.description = this.addTVRDesc;
      tvra.hide_feedback = this.addTVRHideFeedbackS;
      tvra.show_feedback_partner = !this.addTVRHideFeedbackP;
      tvra.start = this.addTVRStart;
      tvra.end = this.addTVREnd;
      tvra.id = this.editTVRID;

      if (this.datesOK(tvra.start, tvra.end)) {
        AssignmentService.saveTVRAssignment(tvra)
          .then(() => {
            if (tvra.id) {
              Notie.success("Edited Team Value Rubric assignment", tvra.name);
            } else {
              Notie.success("Added Team Value Rubric assignment", tvra.name);
            }
            this.loadContext();
            this.$bvModal.hide("addTVRModal");
          })
          .catch((e) => {
            console.log(e);
            if (tvra.id) {
              Notie.error("Failed to edit Team Value Rubric assignment", e);
            } else {
              Notie.error("Failed to add Team Value Rubric assignment", e);
            }
          });
      }
    },

    openDeleteTVRModal(tvra) {
      this.deletingTVR = tvra;
      this.$bvModal.show("deleteTVRModal");
    },

    deleteTVR(tvra) {
      AssignmentService.deleteTVRAssignment(tvra.id)
        .then(() => {
          Notie.success("Deleted Team Value Rubric assignment", tvra.name);
          this.loadContext();
          this.$bvModal.hide("deleteTVRModal");
          this.deleteConfim = null;
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to delete Team Value Rubric assignment", e);
        });
    },

    blankCanvasAssignment() {
      let tvra = {
        name: "New Canvas",
        district_id: this.user.district_id,
        section_id: this.context.id,
      };

      return tvra;
    },

    openAddCanvasModal() {
      this.addCanvasName = null;
      this.addCanvasDesc = null;
      this.addCanvasStart = null;
      this.addCanvasEnd = null;
      this.addCanvasHidePartner = true;
      this.editCanvasID = null;
      this.initAddCanvasDesc = "";
      this.$bvModal.show("addCanvasModal");
    },

    openEditCanvasModal(ca) {
      this.addCanvasName = ca.name;
      this.addCanvasDesc = ca.description;
      this.addCanvasStart = ca.start;
      this.addCanvasEnd = ca.end;
      this.editCanvasID = ca.id;
      this.initAddCanvasDesc = ca.description;
      this.addCanvasHidePartner = !ca.show_partner;
      this.$bvModal.show("addCanvasModal");
    },

    addCanvas() {
      let ca = this.blankCanvasAssignment();
      ca.name = this.addCanvasName;
      ca.description = this.addCanvasDesc;
      ca.start = this.addCanvasStart;
      ca.end = this.addCanvasEnd;
      ca.id = this.editCanvasID;
      ca.show_partner = !this.addCanvasHidePartner;


      if (this.datesOK(ca.start, ca.end)) {
        AssignmentService.saveCanvasAssignment(ca)
          .then(() => {
            if (ca.id) {
              Notie.success("Edited Canvas assignment", ca.name);
            } else {
              Notie.success("Added Canvas assignment", ca.name);
            }
            this.loadContext();
            this.$bvModal.hide("addCanvasModal");
          })
          .catch((e) => {
            console.log(e);
            if (ca.id) {
              Notie.error("Failed to edit Canvas assignment", e);
            } else {
              Notie.error("Failed to add Canvas assignment", e);
            }
          });
      }
    },

    openDeleteCanvasModal(ca) {
      this.deletingCanvas = ca;
      this.$bvModal.show("deleteCanvasModal");
    },

    deleteCanvas(ca) {
      AssignmentService.deleteCanvasAssignment(ca.id)
        .then(() => {
          Notie.success("Deleted Team Value Rubric assignment", ca.name);
          this.loadContext();
          this.$bvModal.hide("deleteCanvasModal");
          this.deleteConfim = null;
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to delete Team Value Rubric assignment", e);
        });
    },

    openAddTeacherModal() {
      this.$bvModal.show("addTeacherModal");
    },

    addTeacher(teacher) {
      SectionService.addTeacher(teacher.id)
        .then(() => {
          Notie.info(`Teacher "${fs.displayName(teacher.name)}" added`);
          this.$bvModal.hide("addTeacherModal");
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to add teacher", e);
        });
    },

    removeTeacher(teacher) {
      SectionService.removeTeacher(teacher.id)
        .then(() => {
          Notie.info(`Teacher "${fs.displayName(teacher.name)}" removed`);
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to remove teacher", e);
        });
    },

    openAddContestTeamModal() {
      this.$bvModal.show("addContestTeamModal");
    },

    openInviteMembersModal(team) {
      this.editingTeam = team;
      this.editingTeamId = team.id;
      this.editingTeamStudents = team.students;
      this.editingTeamJoinCode = team.join_code;
      this.$bvModal.show("inviteMembersModal");
    },

    addTeamMember(student) {
      SectionService.addStudent(student.id)
        .then(() => {
          TeamService.addStudent(this.editingTeam.id, student.id)
            .then(() => {
              Notie.info(`Student "${fs.displayName(student.name)}" added`);
              this.$bvModal.hide("inviteMembersModal");

              this.editingTeam = null;
              this.editingTeamStudents = null;
              this.editingTeamJoinCode = null;
              this.loadContext();
            })
            .catch((e) => {
              console.log(e);
              Notie.error("Failed to add student", e);
            });
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to add student", e);
        });
    },

    openEditTeamModal(team) {
      this.editingTeam = team;
      this.editingTeamStudents = team.students;
      this.editingTeamJoinCode = team.join_code;
      this.editingTeamName = team.name;
      this.editingTeamDescription = team.description;
      this.$bvModal.show("editTeamModal");
    },

    saveTeam() {
      this.editingTeam.students = this.editingTeamStudents;
      this.editingTeam.join_code = this.editingTeamJoinCode;
      this.editingTeam.name = this.editingTeamName;
      this.editingTeam.description = this.editingTeamDescription;
      TeamService.addTeam(this.editingTeam)
        .then(() => {
          Notie.info("Team saved");

          this.editingTeam = null;
          this.editingTeamStudents = null;
          this.editingTeamJoinCode = null;
          this.editingTeamName = null;
          this.editingTeamDescription = null;

          this.$bvModal.hide("editTeamModal");
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to save team");
        });
    },

    openAddStudentModal() {
      this.$bvModal.show("addStudentModal");
    },

    addStudent(student) {
      SectionService.addStudent(student.id)
        .then(() => {
          Notie.info(`Student "${fs.displayName(student.name)}" added`);
          this.$bvModal.hide("addStudentModal");
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to add student", e);
        });
    },

    removeStudent(student) {
      //remove student from teams
      TeamService.unassignTeam(this.context.id, student.id).catch((e) => {
        console.log(e);
        Notie.error("Failed to unassign student from team");
      });

      //remove student
      SectionService.removeStudent(student.id)
        .then(() => {
          Notie.info(`Student "${fs.displayName(student.name)}" removed`);
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to remove student", e);
        });
    },

    openAddPartnerModal() {
      this.$bvModal.show("addPartnerModal");
    },

    addPartner(partner) {
      SectionService.addPartner(partner.id)
        .then(() => {
          Notie.info(`Partner "${fs.displayName(partner.name)}" added`);
          this.$bvModal.hide("addPartnerModal");
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to add partner", e);
        });
    },

    removePartner(partner) {
      SectionService.removePartner(partner.id)
        .then(() => {
          Notie.info(`Partner "${fs.displayName(partner.name)}" removed`);
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to remove partner", e);
        });
    },

    openAddJudgeModal() {
      this.$bvModal.show("addJudgeModal");
    },

    addJudge(judge) {
      SectionService.addJudge(judge.id)
        .then(() => {
          Notie.info(`Judge "${fs.displayName(judge.name)}" added`);
          this.$bvModal.hide("addJudgeModal");
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to add judge", e);
        });
    },

    removeJudge(judge) {
      SectionService.removeJudge(judge.id)
        .then(() => {
          Notie.info(`Judge "${fs.displayName(judge.name)}" removed`);
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to remove judge", e);
        });
    },

    openRegenerateModal(f) {
      this.regenFunc = f;
      this.$bvModal.show("regenJoinCodeModal");
    },

    regenerateTeamCode() {
      this.$bvModal.hide("regenJoinCodeModal");
      TeamService.regenJoinCode(this.editingTeam.id)
        .then(() => {
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to regenerate join code", e);
        });
    },

    regenerateStudentCode() {
      this.$bvModal.hide("regenJoinCodeModal");
      SectionService.regenerateContextStudentCode()
        .then(() => {
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to regenerate join code", e);
        });
    },

    regeneratePartnerCode() {
      this.$bvModal.hide("regenJoinCodeModal");
      SectionService.regenerateContextPartnerCode()
        .then(() => {
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to regenerate join code", e);
        });
    },

    regenerateJudgeCode() {
      this.$bvModal.hide("regenJoinCodeModal");
      SectionService.regenerateContextJudgeCode()
        .then(() => {
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to regenerate join code", e);
        });
    },

    inviteSent() {
      this.editingTeam = null;
      this.editingTeamStudents = null;
      this.editingTeamJoinCode = null;
      this.loadContext();
    },

    inviteSentJudge() {
      this.loadContext();
    },

    saveJudges() {
      for (var team of this.teams) {
        team.judge_ids = [];
        for (var key of Object.keys(this.teamJudgeGrid[team.id])) {
          if (this.teamJudgeGrid[team.id][key]) {
            team.judge_ids.push(key)
          }
        }
        TeamService.addTeam(team).catch((e) => {
          console.log(e);
          Notie.error("Failed to save team", e);
          return;
        });
      }
      Notie.success("Saved!");
    },

    inviteTeam(team) {
      console.log(team);
      var newTeam = this.blankTeam();
      if (team.name) {
        newTeam.name = team.name;
      }
      newTeam.user_ids = [];
      for (var user of team.selectedUsers) {
        newTeam.user_ids.push(user.id);
        this.addStudent(user);
      }

      TeamService.addTeam(newTeam)
        .then((r) => {
          Notie.success(`Added team ${newTeam.name}`);

          var teamID = r.data;
          TeamService.regenJoinCode(teamID)
            .then(() => {
              for (var user of team.pendingUsers) {
                TeamService.inviteUser(user.email, teamID, this.brand.template)
                  .then(() => {
                    this.loadContext();
                  })
                  .catch((e) => {
                    console.log(e);
                    Notie.error("Failed to invite " + user.email, e);
                  });
              }
            })
            .catch((e) => {
              console.log(e);
              Notie.error("Failed to generate join code", e);
            });
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to add team", e);
        });
    },
    nameList(users) {
      var displayNames = [];
      for (var user of users) {
        displayNames.push(fs.displayName(user.name));
      }
      return displayNames.join(", ");
    },
    emailList(users) {
      var emails = [];
      for (var user of users) {
        emails.push(user.email);
      }
      return emails.join(", ");
    },

    numProfiles(student_id) {
      return this.profiles.filter((p) => {
        return p.user.id == student_id;
      }).length;
    },

    numPitches(student_id) {
      return this.pitches.filter((p) => {
        return (
          p.students && p.students.filter((s) => {
            return s.id == student_id;
          }).length > 0
        );
      }).length;
    },

    numPrototypes(student_id) {
      return this.prototypes.filter((p) => {
        return (
          p.students && p.students.filter((s) => {
            return s.id == student_id;
          }).length > 0
        );
      }).length;
    },

    numTVRs(student_id) {
      return this.tvrs.filter((tvr) => {
        return tvr.provider.id == student_id;
      }).length;
    },

    numCanvases(student_id) {
      return this.canvases.filter((c) => {
        return (
          c.students && c.students.filter((s) => {
            return s.id == student_id;
          }).length > 0
        );
      }).length;
    },

    portfolioReady(student_id) {
      if (this.program.submission_config) {
        if (
          this.program.submission_config.num_profiles &&
          this.program.submission_config.num_profiles > this.numProfiles(student_id)
        ) {
          return false;
        }
        if (
          this.program.submission_config.num_pitches &&
          this.program.submission_config.num_pitches > this.numPitches(student_id)
        ) {
          return false;
        }
        if (
          this.program.submission_config.num_prototypes &&
          this.program.submission_config.num_prototypes > this.numPrototypes(student_id)
        ) {
          return false;
        }
        if (
          this.program.submission_config.num_tvrs &&
          this.program.submission_config.num_tvrs > this.numTVRs(student_id)
        ) {
          return false;
        }
        if (
          this.program.submission_config.num_canvases &&
          this.program.submission_config.num_canvases > this.numCanvases(student_id)
        ) {
          return false;
        }
      }
      return true;
    },

    openSubmitModal(student) {
      this.submittingStudent = student;
      this.$bvModal.show("submitModal");
    },

    openUnsubmitModal(student) {
      this.submittingStudent = student;
      this.$bvModal.show("unsubmitModal");
    },

    submit(student_id) {
      this.$bvModal.hide("submitModal");
      SectionService.submitPortfolio(this.context.id, student_id)
        .then(() => {
          Notie.success("Student portfolio submitted");
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to submit student portfolio", e);
        });
    },

    unsubmit(student_id) {
      this.$bvModal.hide("unsubmitModal");
      SectionService.unsubmitPortfolio(this.context.id, student_id)
        .then(() => {
          Notie.success("Student portfolio unsubmitted");
          this.loadContext();
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to unsubmit student portfolio", e);
        });
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.clickable-card {
  transition: transform 0.15s ease-in-out;
  background-color: #f7f7f7;
  width: calc(50% - 40px);
  margin-left: 20px;
  margin-right: 20px;
}

.clickable-card:hover {
  transform: scale(1.05);
}

.join-code {
  font-size: 32px;
  text-align: center;
}

.judge-header{
  padding:10px;
  max-width: 200px;
  word-wrap:break-word;
  text-align: left;
}

.judge-row-label{
  padding: 5px;
  max-width: 400px;
  word-wrap:break-word;
  float: right;
  text-align:right;
}
</style>
